import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faCheck, faCreditCard, faStar, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../Button';
import { NumericKeypad } from '../Keypad/NumericKeypad';
import { formatMoney } from '../../utils/money';
import classes from './value-entry-modal.module.scss';

// Shared %/₪ entry modal — the canonical discount-modal design, reused for tips (and later discounts).
// Caller adapts it per feature via config (labels, presets, scope cards, caps) and reads the result.
export type ValueEntryMode = 'percentage' | 'amount';
export type ValueEntryScope = 'all' | 'snapshot';

export type ValueEntryResult = {
  mode: ValueEntryMode;
  scope: ValueEntryScope; // 'snapshot' for amount mode or one-time percent; 'all' for a live percent
  rawInput: number; // the typed number (percent or ₪)
  amount: number; // resolved ₪ amount, capped
};

export type ValueEntryScopeConfig = {
  allLabel: string;
  allSub: string;
  snapshotLabel: string;
  snapshotSub: string;
};

export type ValueEntryModalProps = {
  isOpen: boolean;
  title: string;
  /** the ₪ basis a percentage resolves against (cart/items total for tip; remaining for discount) */
  percentBase: number;
  /** right-hand info cell (omit both to render only the entered-value cell, e.g. quick charge) */
  infoRightLabel?: string;
  infoRightValue?: number;
  modeLabels?: { percentage: string; amount: string };
  /** amount-only mode: hide the %/₪ toggle and lock to ₪ entry (e.g. split-pay amount) */
  amountOnly?: boolean;
  hideModeToggle?: boolean;
  amountOffset?: number;
  /** which tab is selected on open (ignored when amountOnly); defaults to percentage */
  initialMode?: ValueEntryMode;
  /** seed the ₪ buffer on open; the first keypress replaces it (prefill-then-replace) */
  prefillAmount?: number;
  /** info-row label describing the entered value (distinct from the toggle labels) */
  inputLabels?: { percentage: string; amount: string };
  /** optional quick-pick chips per mode */
  presets?: { percentage: number[]; amount: number[] };
  /** optional scope cards (rendered in percentage mode only) */
  scope?: ValueEntryScopeConfig;
  maxPercent?: number;
  /** ₪ ceiling for amount mode and resolved percent */
  maxAmount?: number;
  capPercentMessage?: string;
  capAmountMessage?: string;
  /** custom content between the header and body (e.g. a step indicator) */
  headerSlot?: React.ReactNode;
  /** legacy dark-blue header (discount modals keep the old design) */
  navyHeader?: boolean;
  /** existing value (₪) — shows the remove button and (when allowAddToExisting) the override UI */
  existingAmount?: number;
  allowAddToExisting?: boolean;
  /** show the "will override" warning on an existing value without offering the add-to-existing card */
  showReplaceWarning?: boolean;
  removeLabel?: string;
  /** shown instead of the remove button when the current user isn't allowed to remove (e.g. non-manager) */
  removeDisabledMessage?: string;
  addToExistingLabel?: string;
  overrideReplaceMessage?: string;
  overrideAddMessage?: string;
  confirmLabel?: string;
  /** allow confirming an exact 0 (e.g. quick charge with amount 0 → tip-only) */
  allowZero?: boolean;
  /** root testid; children derive {id}-amount-value / -confirm / -cancel / -decimal-key */
  dataTestId?: string;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: (result: ValueEntryResult) => void;
  onRemove?: () => void;
};

const MAX_DIGITS = 6;
const round1 = (value: number) => Math.round(value * 10) / 10;

const ValueEntryModalComponent: React.FC<ValueEntryModalProps> = ({
  isOpen,
  title,
  percentBase,
  infoRightLabel,
  infoRightValue,
  modeLabels = { percentage: '% אחוז', amount: '₪ סכום' },
  amountOnly = false,
  hideModeToggle = false,
  amountOffset,
  initialMode = 'percentage',
  prefillAmount,
  inputLabels = { percentage: 'אחוז', amount: 'סכום' },
  presets,
  scope,
  maxPercent = 100,
  maxAmount,
  capPercentMessage = 'הערך חייב להיות בין 0 ל-100',
  capAmountMessage = 'הסכום גבוה מהמותר',
  headerSlot,
  navyHeader = false,
  existingAmount = 0,
  allowAddToExisting = false,
  showReplaceWarning = false,
  removeLabel,
  removeDisabledMessage,
  addToExistingLabel = 'הוסף לקיים',
  overrideReplaceMessage = 'קיים ערך פעיל, הפעולה תדרוס אותו',
  overrideAddMessage = 'הערך החדש יתווסף לקיים',
  confirmLabel = 'אישור',
  allowZero = false,
  dataTestId,
  isSaving = false,
  onClose,
  onConfirm,
  onRemove,
}) => {
  const [mode, setMode] = useState<ValueEntryMode>(amountOnly ? 'amount' : initialMode);
  const [scopeValue, setScopeValue] = useState<ValueEntryScope>('all');
  const [buffer, setBuffer] = useState<string>('');
  const [addToExisting, setAddToExisting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const hasInteractedRef = useRef(false);

  const hasExisting = existingAmount > 0;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (!wasOpenRef.current) {
      // Fresh open: reset per-session state.
      wasOpenRef.current = true;
      hasInteractedRef.current = false;
      setMode(amountOnly ? 'amount' : initialMode);
      setScopeValue('all');
      setAddToExisting(false);
      setValidationError(null);
    }
    // (Re)seed the prefill until the user types; once they interact, never overwrite their input.
    if (!hasInteractedRef.current) {
      setBuffer(prefillAmount != null && prefillAmount > 0 ? String(round1(prefillAmount)) : '');
    }
  }, [isOpen, prefillAmount, amountOnly, initialMode]);

  if (!isOpen) return null;

  const numericValue = buffer === '' ? 0 : Number(buffer);
  const requestedAmount = mode === 'amount'
    ? amountOffset == null ? numericValue : round1(numericValue - amountOffset)
    : round1((percentBase * numericValue) / 100);
  const presetList = presets ? presets[mode] : null;

  // Live cap: disable confirm + show the message once the input exceeds a limit (not on confirm).
  const percentExceeded = mode === 'percentage' && numericValue > maxPercent;
  const amountExceeded = maxAmount != null && requestedAmount > maxAmount;
  const belowBase = amountOffset != null && numericValue < amountOffset;
  const capExceeded = percentExceeded || amountExceeded || belowBase;
  const shownError = validationError ?? (belowBase ? 'הסכום חייב להיות לפחות סכום הבסיס' : percentExceeded ? capPercentMessage : amountExceeded ? capAmountMessage : null);

  const setFromPreset = (value: number) => {
    setValidationError(null);
    hasInteractedRef.current = true;
    setBuffer(String(value));
  };

  const pressDigit = (digit: string) => {
    setValidationError(null);
    // Decide replace-vs-append and flip the ref OUTSIDE the updater — the updater must stay pure
    // (StrictMode double-invokes it; a ref mutation inside would make the 2nd run append to the prefill).
    const replace = !hasInteractedRef.current;
    hasInteractedRef.current = true;
    setBuffer((prev) => {
      const candidate = (replace ? '' : prev) + digit;
      if (candidate.length > MAX_DIGITS) return prev;
      return candidate.replace(/^0(\d)/, '$1');
    });
  };

  const pressDot = () => {
    setValidationError(null);
    const replace = !hasInteractedRef.current;
    hasInteractedRef.current = true;
    setBuffer((prev) => {
      const base = replace ? '' : prev;
      if (base.includes('.')) return base;
      return base === '' ? '0.' : base + '.';
    });
  };

  const pressBackspace = () => {
    setValidationError(null);
    hasInteractedRef.current = true;
    setBuffer((prev) => prev.slice(0, -1));
  };

  const switchMode = (next: ValueEntryMode) => {
    setMode(next);
    setBuffer('');
    setValidationError(null);
  };

  const handleConfirm = () => {
    if (isSaving || capExceeded) return;
    if (mode === 'percentage' && numericValue > maxPercent) {
      setValidationError(capPercentMessage);
      return;
    }
    if (maxAmount != null && requestedAmount > maxAmount) {
      setValidationError(capAmountMessage);
      return;
    }
    // amount is always a one-time snapshot; percentage follows the scope choice (default live 'all')
    const resolvedScope: ValueEntryScope = mode === 'amount' ? 'snapshot' : scope ? scopeValue : 'all';
    if (mode === 'amount' && allowAddToExisting && hasExisting && addToExisting) {
      const combined = round1(existingAmount + requestedAmount);
      onConfirm({ mode, scope: resolvedScope, rawInput: combined, amount: combined });
      return;
    }
    onConfirm({ mode, scope: resolvedScope, rawInput: numericValue, amount: requestedAmount });
  };

  const displayValue = buffer === '' ? '0' : buffer;

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={title} data-testid={dataTestId}>
      <div className={classes.modal}>
        <div className={`${classes.header} ${navyHeader ? classes.headerNavy : ''}`}>
          <h2 className={classes.title}>{title}</h2>
        </div>

        {headerSlot}

        <div className={classes.body}>
          {!amountOnly && !hideModeToggle && (
            <div className={classes.toggle}>
              <button
                type="button"
                className={`${classes.toggleBtn} ${mode === 'amount' ? classes.toggleActive : ''}`}
                data-testid={dataTestId ? `${dataTestId}-mode-amount` : undefined}
                onClick={() => switchMode('amount')}
              >
                {modeLabels.amount}
              </button>
              <button
                type="button"
                className={`${classes.toggleBtn} ${mode === 'percentage' ? classes.toggleActive : ''}`}
                data-testid={dataTestId ? `${dataTestId}-mode-percentage` : undefined}
                onClick={() => switchMode('percentage')}
              >
                {modeLabels.percentage}
              </button>
            </div>
          )}

          {presetList && presetList.length > 0 && (
            <div className={classes.presets}>
              {presetList.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`${classes.presetChip} ${buffer === String(preset) ? classes.presetChipOn : ''}`}
                  data-testid={dataTestId ? `${dataTestId}-preset-${preset}` : undefined}
                  onClick={() => setFromPreset(preset)}
                >
                  {mode === 'percentage' ? `${preset}%` : preset}
                </button>
              ))}
            </div>
          )}

          {hasExisting && (allowAddToExisting || showReplaceWarning) && (
            <div className={`${classes.overrideWarning} ${addToExisting ? classes.overrideWarningAdd : ''}`} role="alert">
              <span className={classes.overrideWarningIcon}>{addToExisting ? '✓' : '⚠'}</span>
              {allowAddToExisting && addToExisting ? overrideAddMessage : overrideReplaceMessage}
            </div>
          )}

          {shownError && (
            <div className={classes.overrideWarning} role="alert">
              <span className={classes.overrideWarningIcon}>⚠</span>
              {shownError}
            </div>
          )}

          {mode === 'amount' && allowAddToExisting && hasExisting && (
            <button
              type="button"
              className={`${classes.addToExistingCard} ${addToExisting ? classes.addToExistingCardActive : ''}`}
              data-testid={dataTestId ? `${dataTestId}-add-to-existing` : undefined}
              onClick={() => { setAddToExisting((v) => !v); setValidationError(null); }}
            >
              <span className={`${classes.addToExistingCheck} ${addToExisting ? classes.addToExistingCheckVisible : ''}`} aria-hidden="true">
                <FontAwesomeIcon icon={faCheck as IconProp} />
              </span>
              {addToExistingLabel}
            </button>
          )}

          {scope && mode === 'percentage' && (
            <div className={classes.scopeCards}>
              <button
                type="button"
                className={`${classes.scopeCard} ${scopeValue === 'all' ? classes.scopeCardActive : ''}`}
                data-testid={dataTestId ? `${dataTestId}-scope-all` : undefined}
                onClick={() => { setScopeValue('all'); setValidationError(null); }}
              >
                <div className={classes.scopeCardTop}>
                  <span className={`${classes.scopeCheck} ${scopeValue === 'all' ? classes.scopeCheckVisible : ''}`} aria-hidden="true">
                    <FontAwesomeIcon icon={faCheck as IconProp} />
                  </span>
                  <span className={classes.scopeIcon} aria-hidden="true">
                    <FontAwesomeIcon icon={faCreditCard as IconProp} />
                  </span>
                </div>
                <span className={classes.scopeTitle}>{scope.allLabel}</span>
                <span className={classes.scopeSub}>{scope.allSub}</span>
              </button>
              <button
                type="button"
                className={`${classes.scopeCard} ${scopeValue === 'snapshot' ? classes.scopeCardActive : ''}`}
                data-testid={dataTestId ? `${dataTestId}-scope-snapshot` : undefined}
                onClick={() => { setScopeValue('snapshot'); setValidationError(null); }}
              >
                <div className={classes.scopeCardTop}>
                  <span className={`${classes.scopeCheck} ${scopeValue === 'snapshot' ? classes.scopeCheckVisible : ''}`} aria-hidden="true">
                    <FontAwesomeIcon icon={faCheck as IconProp} />
                  </span>
                  <span className={classes.scopeIcon} aria-hidden="true">
                    <FontAwesomeIcon icon={faStar as IconProp} />
                  </span>
                </div>
                <span className={classes.scopeTitle}>{scope.snapshotLabel}</span>
                <span className={classes.scopeSub}>{scope.snapshotSub}</span>
              </button>
            </div>
          )}

          <div className={classes.infoRow}>
            <div className={classes.infoCell}>
              <span className={classes.infoCellLabel}>{mode === 'percentage' ? inputLabels.percentage : inputLabels.amount}</span>
              <span
                className={classes.infoCellValue}
                data-testid={dataTestId ? `${dataTestId}-amount-value` : undefined}
              >
                {mode === 'percentage' ? `${displayValue}%` : `₪ ${displayValue}`}
              </span>
            </div>
            {infoRightLabel != null && (
              <div className={classes.infoCellRight}>
                <span className={classes.infoCellLabel}>{infoRightLabel}</span>
                <span className={classes.infoCellValue}>₪ {formatMoney(infoRightValue ?? 0)}</span>
              </div>
            )}
          </div>

          {amountOffset != null && (
            <small className={classes.addedTip}>טיפ שיתווסף: ₪ {formatMoney(Math.max(0, requestedAmount))}</small>
          )}

          <NumericKeypad
            keypadClassName={classes.keypad}
            keyClassName={classes.key}
            dataTestId={dataTestId ? `${dataTestId}-keypad` : undefined}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceDisabled={buffer === ''}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={{
              label: '.',
              className: classes.key,
              onClick: pressDot,
              ariaLabel: 'נקודה עשרונית',
              dataTestId: dataTestId ? `${dataTestId}-decimal-key` : undefined,
            }}
          />

          <div className={classes.actions}>
            <Button
              variant="cancel"
              fullWidth
              onClick={onClose}
              disabled={isSaving}
              dataTestId={dataTestId ? `${dataTestId}-cancel` : undefined}
            >
              ביטול
            </Button>
            <Button
              variant="confirm"
              fullWidth
              onClick={handleConfirm}
              disabled={(allowZero ? numericValue < 0 : numericValue <= 0) || isSaving || capExceeded}
              dataTestId={dataTestId ? `${dataTestId}-confirm` : undefined}
            >
              {isSaving ? 'שומר...' : confirmLabel}
            </Button>
          </div>

          {hasExisting && removeDisabledMessage && (
            <p className={classes.removeDisabledMessage}>{removeDisabledMessage}</p>
          )}

          {hasExisting && !removeDisabledMessage && onRemove && removeLabel && (
            <button type="button" className={classes.removeButton} data-testid={dataTestId ? `${dataTestId}-remove` : undefined} onClick={onRemove} disabled={isSaving}>
              <FontAwesomeIcon icon={faTrash as IconProp} />
              {removeLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const ValueEntryModal = React.memo(ValueEntryModalComponent);
